/** @jsxImportSource react */
import React, { useEffect, useState, FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from '../components/ui/Toast'
import { useAuth } from '../contexts/auth'
import { api } from '../services/api'
import { useTranslation } from '../i18n'
import { Button } from '../components/ui/Button'
import { COUNTRY_CODES } from '../constants';
import { User } from '../types';
import {
  User as UserIcon,
  Mail,
  Phone,
  Lock,
  CheckCircle,
  Shield,
  AlertCircle,
  Eye,
  EyeOff,
  MapPin,
} from 'lucide-react'

const ProfilePage = () => {
  const { user, updateUser } = useAuth()
  const { t } = useTranslation()
  // State for personal information form
  const [name, setName] = useState(user?.name || '')
  const [email, setEmail] = useState(user?.email || '')
  const [city, setCity] = useState(user?.city || '')
  const [countryCode, setCountryCode] = useState(user?.countryCode || '+964')
  const [phone, setPhone] = useState(user?.phone || '')
  // State for password change form
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  // Update form when user data changes
  useEffect(() => {
    if (user) {
      setName(user.name || '')
      setEmail(user.email || '')
      setCity(user.city || '')
      setCountryCode(user.countryCode || '+964')
      setPhone(user.phone || '')
    }
  }, [user])
  
  const profileMutation = useMutation({
    mutationFn: (updates: Partial<User>) => api.updateProfile(updates),
    onSuccess: (updatedUser) => {
      toast.success(t('profileUpdatedSuccess'));
      updateUser(updatedUser);
    },
    onError: () => toast.error(t('unexpectedError')),
  });
  
  const passwordMutation = useMutation({
    mutationFn: () =>
      api.changePassword({
        currentPassword,
        newPassword,
      }),
    onSuccess: () => {
      toast.success(
        t('passwordUpdatedSuccess') || 'Password updated successfully',
      )
      setCurrentPassword('')
      setNewPassword('')
      setConfirmNewPassword('')
    },
    onError: (error: Error) => {
      const message = error.message?.includes('Incorrect current password')
        ? t('incorrectCurrentPassword') || 'Incorrect current password'
        : t('unexpectedError') || 'An unexpected error occurred'
      toast.error(message)
    },
  })
  
  const handleProfileSubmit = (e: FormEvent) => {
    e.preventDefault()

    const profileUpdates: Partial<User> = {};
    if (name !== user?.name) profileUpdates.name = name;
    if (city !== user?.city) profileUpdates.city = city;
    if (countryCode !== user?.countryCode) profileUpdates.countryCode = countryCode;
    if (phone !== user?.phone) profileUpdates.phone = phone;

    if (Object.keys(profileUpdates).length > 0) {
        profileMutation.mutate(profileUpdates);
    } else {
        toast.info(t('noChangesToSave'));
    }
  }
  const handlePasswordSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (newPassword !== confirmNewPassword) {
      toast.error(t('passwordMismatch') || 'Passwords do not match')
      return;
    }
    if (newPassword.length < 6) {
        toast.error(t('weakPasswordError'));
        return;
    }
    passwordMutation.mutate()
  }
  if (!user) return null
  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-secondary-800 mb-2">
          {t('profile') || 'Profile'}
        </h1>
        <p className="text-secondary-500 max-w-3xl">
          {t('profileDescription') ||
            'Manage your personal information and update your password.'}
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Sidebar with user info summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6 sticky top-24">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 mb-4">
                <span className="text-3xl font-bold">
                  {name.charAt(0).toUpperCase()}
                </span>
              </div>
              <h2 className="text-xl font-semibold text-secondary-800">{name}</h2>
              <p className="text-secondary-500 mt-1">{user.email}</p>
              {user.teCode && (
                <div className="mt-4 px-3 py-1.5 bg-secondary-100 rounded-full text-secondary-700 text-sm font-medium">
                  {user.teCode}
                </div>
              )}
            </div>
            <div className="border-t border-secondary-200 pt-6 space-y-4">
              <div className="flex items-center text-secondary-600">
                <Mail className="w-5 h-5 me-3 text-primary-500" />
                <span className="text-sm">
                  {user.isEmailVerified ? (
                    <span className="flex items-center text-primary-600">
                      <CheckCircle className="w-4 h-4 me-1" />
                      {t('emailVerified') || 'Email verified'}
                    </span>
                  ) : (
                    <span className="flex items-center text-secondary-600">
                      <AlertCircle className="w-4 h-4 me-1" />
                      {t('emailNotVerified') || 'Email not verified'}
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center text-secondary-600">
                <MapPin className="w-5 h-5 me-3 text-primary-500" />
                <span className="text-sm">
                  {user.city || t('notProvided')}
                </span>
              </div>
              <div className="flex items-center text-secondary-600">
                <Phone className="w-5 h-5 me-3 text-primary-500" />
                <span className="text-sm">
                  {user.phone || t('notProvided')}
                </span>
              </div>
              <div className="flex items-center text-secondary-600">
                <Shield className="w-5 h-5 me-3 text-primary-500" />
                <span className="text-sm">
                  {t('accountType') || 'Account type'}:{' '}
                  <strong>{user.role}</strong>
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Main content with forms */}
        <div className="lg:col-span-2 space-y-8">
          {/* Personal Information Card */}
          <div className="bg-white rounded-xl shadow-sm border border-secondary-200 overflow-hidden">
            <div className="bg-gradient-to-r from-white to-secondary-50 border-b border-secondary-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-secondary-800 flex items-center">
                <UserIcon className="w-5 h-5 me-2 text-primary-500" />
                {t('personalInformation') || 'Personal Information'}
              </h2>
            </div>
            <form onSubmit={handleProfileSubmit} className="p-6">
              <div className="space-y-6">
                <div>
                  <label
                    className="block text-sm font-medium text-secondary-700 mb-1"
                    htmlFor="name"
                  >
                    {t('fullNameLabel') || 'Full Name'}
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                      <UserIcon className="h-5 w-5 text-secondary-400" />
                    </div>
                    <input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={
                        t('fullNamePlaceholder')
                      }
                      required
                      className={`block w-full py-2.5 border border-secondary-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ps-10 pe-3`}
                    />
                  </div>
                </div>
                <div>
                    <label
                        className="block text-sm font-medium text-secondary-700 mb-1"
                        htmlFor="city"
                    >
                        {t('cityLabel')}
                    </label>
                    <div className="relative">
                        <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                            <MapPin className="h-5 w-5 text-secondary-400" />
                        </div>
                        <input
                            id="city"
                            type="text"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            placeholder={t('cityPlaceholder')}
                            className={`block w-full py-2.5 border border-secondary-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ps-10 pe-3`}
                        />
                    </div>
                </div>
                <div>
                  <label
                    className="block text-sm font-medium text-secondary-700 mb-1"
                    htmlFor="email"
                  >
                    {t('email_label') || 'Email Address'}
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                      <Mail className="h-5 w-5 text-secondary-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`block w-full py-2.5 border border-secondary-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ps-10 pe-3 bg-secondary-100 cursor-not-allowed`}
                      disabled
                    />
                  </div>
                  <p className="text-xs text-secondary-500 mt-1.5">{t('adminOnlyEmailChange')}</p>
                </div>
                <div>
                  <label
                    className="block text-sm font-medium text-secondary-700 mb-1"
                    htmlFor="phone"
                  >
                    {t('phoneNumberLabel') || 'Phone Number'}
                  </label>
                    <div className="flex">
                        <select
                            value={countryCode}
                            onChange={(e) => setCountryCode(e.target.value)}
                            className="appearance-none rounded-s-lg rtl:rounded-s-none rtl:rounded-e-lg border border-e-0 rtl:border-e-auto rtl:border-s-0 border-gray-300 bg-gray-100 px-3 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                        >
                            {COUNTRY_CODES.map((c) => (
                                <option key={c.code} value={c.code}>
                                    {c.code}
                                </option>
                            ))}
                        </select>
                        <div className="relative flex-1">
                            <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                                <Phone size={18} className="text-gray-400" />
                            </div>
                            <input
                                id="phone"
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder={t('phonePlaceholder')}
                                className={`w-full rounded-e-lg rtl:rounded-e-none rtl:rounded-s-lg border border-gray-300 py-3 ps-10 pe-4 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200`}
                            />
                        </div>
                    </div>
                </div>
              </div>
              <div className="mt-8 flex justify-end">
                <Button
                  type="submit"
                  disabled={profileMutation.isPending}
                  isLoading={profileMutation.isPending}
                  className="px-6"
                >
                  {t('saveChanges') || 'Save Changes'}
                </Button>
              </div>
            </form>
          </div>
          {/* Change Password Card */}
          <div className="bg-white rounded-xl shadow-sm border border-secondary-200 overflow-hidden">
            <div className="bg-gradient-to-r from-white to-secondary-50 border-b border-secondary-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-secondary-800 flex items-center">
                <Lock className="w-5 h-5 me-2 text-primary-500" />
                {t('changePassword') || 'Change Password'}
              </h2>
            </div>
            <form onSubmit={handlePasswordSubmit} className="p-6">
              <div className="space-y-6">
                <div>
                  <label
                    className="block text-sm font-medium text-secondary-700 mb-1"
                    htmlFor="currentPassword"
                  >
                    {t('currentPassword') || 'Current Password'}
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                      <Lock className="h-5 w-5 text-secondary-400" />
                    </div>
                    <input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder={t('currentPasswordPlaceholder')}
                      required
                      className={`block w-full border border-secondary-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ps-10 pe-10 py-2.5`}
                    />
                    <button
                      type="button"
                      className={`absolute inset-y-0 end-3 flex items-center`}
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-5 w-5 text-secondary-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-secondary-400" />
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <label
                    className="block text-sm font-medium text-secondary-700 mb-1"
                    htmlFor="newPassword"
                  >
                    {t('newPassword') || 'New Password'}
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                      <Lock className="h-5 w-5 text-secondary-400" />
                    </div>
                    <input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t('newPasswordPlaceholder')}
                      required
                      className={`block w-full border border-secondary-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ps-10 pe-10 py-2.5`}
                    />
                    <button
                      type="button"
                      className={`absolute inset-y-0 end-3 flex items-center`}
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-5 w-5 text-secondary-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-secondary-400" />
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <label
                    className="block text-sm font-medium text-secondary-700 mb-1"
                    htmlFor="confirmNewPassword"
                  >
                    {t('confirmNewPassword') || 'Confirm New Password'}
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                      <Lock className="h-5 w-5 text-secondary-400" />
                    </div>
                    <input
                      id="confirmNewPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      placeholder={t('newPasswordPlaceholder')}
                      required
                      className={`block w-full border border-secondary-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ps-10 pe-10 py-2.5`}
                    />
                    <button
                      type="button"
                      className={`absolute inset-y-0 end-3 flex items-center`}
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-secondary-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-secondary-400" />
                      )}
                    </button>
                  </div>
                </div>
                {newPassword &&
                  confirmNewPassword &&
                  newPassword !== confirmNewPassword && (
                    <div className="p-3 bg-secondary-100 border border-secondary-200 rounded-lg text-secondary-700 text-sm flex items-center">
                      <AlertCircle className="w-5 h-5 me-2 text-secondary-500" />
                      {t('passwordMismatch') || 'Passwords do not match'}
                    </div>
                  )}
                {newPassword && newPassword.length < 6 && (
                  <div className="p-3 bg-secondary-100 border border-secondary-200 rounded-lg text-secondary-700 text-sm flex items-center">
                    <AlertCircle className="w-5 h-5 me-2 text-secondary-500" />
                    {t('weakPasswordError')}
                  </div>
                )}
              </div>
              <div className="mt-8 flex justify-end">
                <Button
                  type="submit"
                  disabled={
                    passwordMutation.isPending ||
                    !newPassword ||
                    newPassword.length < 6 ||
                    newPassword !== confirmNewPassword
                  }
                  isLoading={passwordMutation.isPending}
                  className="px-6"
                >
                  {t('updatePassword') || 'Update Password'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
export default ProfilePage;